import React, { useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { storage } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

import { compressImage } from '../../utils/imageUtils';
import PhotoAnnotator from './PhotoAnnotator';

// Local state textarea to prevent re-render on each keystroke
const DiscrepancyNoteTextarea = ({ initialValue, onSave }) => {
    const [localValue, setLocalValue] = useState(initialValue);

    return (
        <textarea
            placeholder="Wat klopt niet? (bijv. 'Tag V-101 ontbreekt op P&ID')"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => onSave(localValue)}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="mt-2 w-full bg-slate-900 text-white border border-yellow-500/50 rounded-lg p-2 text-xs focus:border-yellow-400 focus:outline-none resize-none"
            rows={2}
        />
    );
};

export default function ImageUpload({
    path,
    onUploadComplete,
    onDelete,
    onSessionDelete, // NEW: Called for inline session deletes (no modal)
    currentImageUrls = [],
    selectedUrls = [],
    availableImages, // NEW: Array of objects {url, label, type}
    onToggleSelect,
    allowDelete = false, // Security: Only allow deleting session uploads by default
    sessionUploadedUrls = [], // To track which can be deleted
    excludeUrls = [], // NEW: URLs to exclude from display (e.g., LOTO photos)

    // INLINE RENAMING PROPS
    unnamedImages = [],
    onRename,

    // TECH DOC DISCREPANCY PROPS
    photoMetadata = {},  // { [url]: { hasDiscrepancy, discrepancyNote } }
    onMetadataChange,    // (url, metadata) => void
    showDiscrepancyUI = false, // Whether to show the discrepancy checkbox

    label = "Foto's toevoegen",
    previewMode = false, // NEW: Disable interactions
    compact = false      // NEW: Minimal UI for checklists
}) {
    // Generate unique ID for this instance to prevent input conflicts
    const instanceId = useId();

    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');

    // Local state for inline renaming inputs
    const [renameValues, setRenameValues] = useState({});

    const handleRenameChange = (url, val) => {
        setRenameValues(prev => ({ ...prev, [url]: val }));
    };

    const submitRename = (url) => {
        const val = renameValues[url];
        if (val && val.trim() && onRename) {
            onRename(url, val.trim());
        }
    };

    // Internal state to track session uploads if parent doesn't provide them
    const [internalSessionUrls, setInternalSessionUrls] = useState([]);

    // State for inline delete confirmation (two-click pattern)
    const [pendingDelete, setPendingDelete] = useState(null);

    // State for photo annotation flow
    const [annotatingImage, setAnnotatingImage] = useState(null); // { blob, file } or null
    const [pendingUploadFiles, setPendingUploadFiles] = useState([]); // remaining files to process after annotation

    // --- SESSION DELETE HELPER (For inline confirmation - NO modal) ---
    const performSessionDelete = async (url) => {
        try {

            // 1. Delete from Firebase Storage
            const storageRef = ref(storage, url);
            await deleteObject(storageRef);

        } catch (err) {
            console.error("Storage delete failed (might be external or already gone):", err);
        }

        // 2. Remove from internal session state
        setInternalSessionUrls(prev => prev.filter(u => u !== url));

        // 3. Call parent's session delete handler (updates parent state without modal)
        if (onSessionDelete) {
            onSessionDelete(url);
        }
    };

    // --- MODAL DELETE HELPER (For library deletes with confirmation modal) ---
    const performDelete = async (url) => {
        try {

            // 1. Delete from Firebase Storage (Real cleanup)
            const storageRef = ref(storage, url);
            await deleteObject(storageRef);

        } catch (err) {
            console.error("Storage delete failed (might be external or already gone):", err);
        }

        // 2. Remove from internal session state
        setInternalSessionUrls(prev => prev.filter(u => u !== url));

        // 3. Notify Parent
        if (onDelete) {
            onDelete(url);
        }
    };

    // Core upload helper — uploads a blob/file to Firebase Storage
    const uploadSingleFile = async (fileToUpload, fileName) => {
        const uniqueName = `${Date.now()}_${fileName}`;
        const storageRef = ref(storage, `${path}/${uniqueName}`);
        const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

        return new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(progress);
                },
                (error) => reject(error),
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                }
            );
        });
    };

    // Complete upload flow — compresses and uploads files, then notifies parent
    const uploadFiles = async (filesToUpload) => {
        setError(null);
        setUploading(true);
        setProgress(0);

        try {
            const uploadPromises = filesToUpload.map(async ({ blob, name }) => {
                let fileToUpload = blob;
                try {
                    const compressedBlob = await compressImage(blob, 800, 800, 0.7);
                    fileToUpload = compressedBlob;
                } catch (compressionError) {
                    console.warn("Image compression failed, uploading original:", compressionError);
                }
                return uploadSingleFile(fileToUpload, name);
            });

            const newUrls = await Promise.all(uploadPromises);

            setInternalSessionUrls(prev => [...prev, ...newUrls]);

            if (window.navigator && window.navigator.vibrate) {
                window.navigator.vibrate(50);
            }

            onUploadComplete(newUrls);
        } catch (err) {
            console.error("Upload error:", err);
            setError("Upload mislukt: " + err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        if (e.target) e.target.value = '';

        // Check if first file is an image — offer annotation
        const firstFile = files[0];
        const isImage = firstFile.type && firstFile.type.startsWith('image/');

        if (isImage && files.length === 1) {
            // Single image — open annotator
            try {
                const compressedBlob = await compressImage(firstFile, 800, 800, 0.7);
                const blobUrl = URL.createObjectURL(compressedBlob);
                setAnnotatingImage({ blobUrl, blob: compressedBlob, name: firstFile.name });
            } catch (err) {
                // Compression failed, use original
                const blobUrl = URL.createObjectURL(firstFile);
                setAnnotatingImage({ blobUrl, blob: firstFile, name: firstFile.name });
            }
            return;
        }

        // Multiple files or non-image — upload directly (original behavior)
        await uploadFiles(files.map(f => ({ blob: f, name: f.name })));
    };

    // Annotation save handler — uploads the annotated version
    const handleAnnotationSave = async (annotatedBlob) => {
        const imgData = annotatingImage;
        URL.revokeObjectURL(imgData.blobUrl);
        setAnnotatingImage(null);

        await uploadFiles([
            { blob: annotatedBlob, name: `annotated_${imgData.name}` },
        ]);
    };

    // Annotation skip handler — uploads the original without annotation
    const handleAnnotationSkip = async () => {
        const imgData = annotatingImage;
        URL.revokeObjectURL(imgData.blobUrl);
        setAnnotatingImage(null);

        await uploadFiles([
            { blob: imgData.blob, name: imgData.name },
        ]);
    };

    // Annotation cancel handler — discard entirely
    const handleAnnotationCancel = () => {
        if (annotatingImage) {
            URL.revokeObjectURL(annotatingImage.blobUrl);
        }
        setAnnotatingImage(null);
    };

    // Helper to normalize URLs for comparison (handling encoding/decoding differences and tokens)
    const isSessionUpload = (url) => {
        // Combine prop-based and internal session URLs
        const allSessionUrls = [...(sessionUploadedUrls || []), ...internalSessionUrls];

        if (allSessionUrls.length === 0) return false;

        // Robust filename matching
        // Firebase Storage URLs are like: .../o/folder%2Ffilename.jpg?alt=...
        // We want to extract 'filename.jpg'
        const getFilename = (u) => {
            if (!u) return '';
            try {
                // 1. Remove query params
                const noQuery = u.split('?')[0];
                // 2. Decode URI component to handle %2F, etc.
                const decoded = decodeURIComponent(noQuery);
                // 3. Get the last part after /
                return decoded.split('/').pop();
            } catch (e) {
                return u; // Fallback
            }
        };

        const targetFilename = getFilename(url);

        const match = allSessionUrls.some(sUrl => {
            if (!sUrl) return false;
            // Direct strict match
            if (sUrl === url) return true;

            // Filename match
            const sourceFilename = getFilename(sUrl);
            return sourceFilename === targetFilename;
        });

        // DEBUG: Only log if it's potentially a session upload but failed strict match
        // console.log(`isSessionUpload Check: ${targetFilename} -> Match: ${match}`);
        return match;
    };

    // Track images that fail to load
    const [deadImageUrls, setDeadImageUrls] = useState([]);

    // Filter images into Selected and Available
    // FIX: Handle Objects {url, label} vs Strings
    const rawAllUrls = [...currentImageUrls, ...internalSessionUrls];

    // Deduplicate by URL
    const allDisplayUrls = [];
    const seenUrls = new Set();

    rawAllUrls.forEach(item => {
        const u = typeof item === 'string' ? item : item.url;
        if (!seenUrls.has(u)) {
            seenUrls.add(u);
            allDisplayUrls.push(item);
        }
    });

    // Apply exclusion filter (for LOTO photos, etc.)
    const excludeUrlsSet = new Set(excludeUrls);

    const cleanImageUrls = allDisplayUrls.filter(item => {
        const u = typeof item === 'string' ? item : item.url;
        // Filter out dead images AND excluded URLs
        return !deadImageUrls.includes(u) && !excludeUrlsSet.has(u);
    });

    const selectedImages = cleanImageUrls.filter(item => {
        const u = typeof item === 'string' ? item : item.url;
        return selectedUrls && selectedUrls.includes(u);
    });

    // FIX: Use prop 'availableImages' if provided (it contains richer data like labels), 
    // otherwise fallback to internal calculation from currentImageUrls.
    // ALSO: Ensure we filter out dead images from availableImages too!
    const rawAvailable = availableImages || cleanImageUrls.filter(item => {
        const u = typeof item === 'string' ? item : item.url;
        return !selectedUrls || !selectedUrls.includes(u);
    });

    const finalAvailableImages = rawAvailable.filter(item => {
        const u = typeof item === 'string' ? item : item.url;
        return !deadImageUrls.includes(u);
    });


    // Lightbox State
    const [zoomImage, setZoomImage] = useState(null);

    return (
        <div className="space-y-8">
            {/* Photo Annotator Modal */}
            {annotatingImage && (
                <PhotoAnnotator
                    imageUrl={annotatingImage.blobUrl}
                    onSave={handleAnnotationSave}
                    onCancel={handleAnnotationCancel}
                    onSkip={handleAnnotationSkip}
                />
            )}
            {/* Lightbox Modal - Using Portal for true fullscreen (escapes parent transforms) */}
            {zoomImage && createPortal(
                <div
                    className="fixed z-[100000] bg-black/95 flex items-center justify-center overflow-hidden backdrop-blur-sm"
                    style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        margin: 0,
                        padding: 0,
                        position: 'fixed'
                    }}
                    onClick={() => setZoomImage(null)}
                >
                    <img
                        src={zoomImage}
                        alt="Zoom"
                        className="max-w-full max-h-full object-contain p-2 md:p-8"
                        style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Optional: Toggle close on image click or keep it for pan/zoom in future?
                            // For now, let's allow closing by clicking outside, preventing close on image click matches standard behavior often
                        }}
                    />
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 z-[100001] bg-black/50 rounded-full p-2 border border-white/20 shadow-lg"
                        onClick={(e) => {
                            e.stopPropagation();
                            setZoomImage(null);
                        }}
                    >
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>,
                document.body
            )}

            {/* COMPACT MODE UI (Single Image Focus) */}
            {compact ? (
                <div className="flex items-center gap-2">
                    {/* 1. Thumbnail / Preview */}
                    {selectedUrls && selectedUrls.length > 0 ? (
                        <div className="relative group shrink-0">
                            <img
                                src={selectedUrls[0]}
                                alt="Selected"
                                className="w-16 h-16 rounded object-cover border-2 border-green-500 cursor-zoom-in"
                                onClick={() => setZoomImage(selectedUrls[0])}
                            />
                            {!previewMode && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Use performDelete if it's a session upload, otherwise just deselect
                                        if (isSessionUpload(selectedUrls[0])) {
                                            if (window.confirm("Foto direct verwijderen uit geheugen en database?")) {
                                                performDelete(selectedUrls[0]);
                                            }
                                        } else {
                                            onToggleSelect && onToggleSelect(selectedUrls[0]);
                                        }
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow-sm hover:bg-red-600 flex items-center justify-center p-1"
                                    title={isSessionUpload(selectedUrls[0]) ? "Deep Delete" : "Deselecteer"}
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="w-16 h-16 rounded bg-slate-800 border-2 border-dashed border-slate-600 flex items-center justify-center shrink-0">
                            <span className="text-2xl opacity-50">📷</span>
                        </div>
                    )}

                    {/* 2. Action Area */}
                    <div className="flex-1 min-w-0">
                        {previewMode ? (
                            <button
                                onClick={() => onUploadComplete(["https://placehold.co/600x400/1e293b/white?text=SIMULATIE+FOTO"])}
                                className="btn btn-sm bg-orange-600 hover:bg-orange-500 text-black font-black w-full border-none shadow-lg animate-pulse scale-[1.02]"
                            >
                                Overbrug Foto Upload ⚡
                            </button>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                {/* BIJLAGEN / UPLOAD */}
                                <div className="relative">
                                    <input
                                        type="file"
                                        id="compact-upload"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        disabled={uploading}
                                    />
                                    <label
                                        htmlFor="compact-upload"
                                        className={`btn btn-sm ${uploading ? 'bg-slate-700 cursor-not-allowed' : 'bg-slate-700 hover:bg-slate-600'} w-full flex items-center justify-center gap-1 text-slate-300 font-black cursor-pointer shadow-lg text-[10px] uppercase tracking-tighter`}
                                    >
                                        <span>📂 Bibliotheek</span>
                                    </label>
                                </div>

                                {/* CAMERA / DIRECT */}
                                <div className="relative">
                                    <input
                                        type="file"
                                        id="compact-camera"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        disabled={uploading}
                                    />
                                    <label
                                        htmlFor="compact-camera"
                                        className={`btn btn-sm ${uploading ? 'bg-slate-700 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'} w-full flex items-center justify-center gap-1 text-black font-black cursor-pointer shadow-lg text-[10px] uppercase tracking-tighter`}
                                    >
                                        {uploading ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : (
                                            <span>📸 Camera</span>
                                        )}
                                    </label>
                                </div>
                            </div>
                        )}
                        {error && <div className="text-[10px] text-red-400 mt-1 truncate">{error}</div>}
                    </div>
                </div>
            ) : (
                /* STANDARD MODE UI */
                <>
                    {/* Section 1: Selected Photos */}
                    <div className="bg-green-900/20 border border-green-700/50 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-green-400 mb-4 flex items-center gap-2">
                            ✅ Geselecteerde Foto's ({selectedImages.length})
                        </h3>

                        {selectedImages.length === 0 ? (
                            <div className="text-center py-8 border-2 border-dashed border-green-800/50 rounded-lg text-green-300/50">
                                Nog geen foto's geselecteerd.
                                <br />
                                <span className="text-sm">Klik op een foto uit de bibliotheek hieronder om te selecteren.</span>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {selectedImages.map((item, index) => {
                                    const url = typeof item === 'string' ? item : item.url;

                                    // FIX: If item is just a URL string, try to find the labeled object in availableImages
                                    let rawItem = item;
                                    if (typeof item === 'string') {
                                        const found = finalAvailableImages.find(ai => (typeof ai === 'object' ? ai.url : ai) === item);
                                        if (found) rawItem = found;
                                    }

                                    const isSession = isSessionUpload(url);
                                    const canDelete = allowDelete || isSession;

                                    // CHECK: Is this image waiting for a name?
                                    const isUnnamed = unnamedImages.includes(url);

                                    return (
                                        <div key={url} className={`flex flex-col bg-slate-800/80 rounded-xl border shadow-xl overflow-hidden relative ${isUnnamed ? 'border-red-500 border-4' : 'border-slate-600'}`}>

                                            {/* UNNAMED HEADER - PUSHES IMAGE DOWN */}
                                            {isUnnamed && (
                                                <div className="bg-slate-900 border-b-4 border-red-500 p-4 flex flex-col gap-3 animate-fade-in z-30">
                                                    <div className="flex items-center gap-2 text-red-400 font-bold text-sm uppercase tracking-wider">
                                                        <span>⚠️</span> Naamgeving Vereist
                                                    </div>

                                                    <div className="flex flex-col gap-2">
                                                        <input
                                                            type="text"
                                                            placeholder="Geef dit onderdeel een naam..."
                                                            className="input input-sm w-full bg-slate-800 text-white border-slate-600 focus:border-red-500 focus:outline-none font-medium"
                                                            value={renameValues[url] || ''}
                                                            onChange={(e) => handleRenameChange(url, e.target.value)}
                                                            autoFocus
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') submitRename(url);
                                                            }}
                                                        />
                                                        <button
                                                            onClick={() => submitRename(url)}
                                                            disabled={!renameValues[url]?.trim()}
                                                            className="btn btn-sm !bg-red-600 hover:!bg-red-500 !text-white border-none w-full font-bold shadow-lg disabled:!bg-slate-700 disabled:!text-slate-500 disabled:border-slate-600"
                                                        >
                                                            Opslaan & Verder
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* LABEL HEADER */}
                                            {typeof rawItem === 'object' && rawItem.label && (
                                                <div className="bg-blue-900/90 text-white text-xs font-bold px-3 py-2 text-center border-b border-blue-500/50 truncate">
                                                    📌 {rawItem.label}
                                                </div>
                                            )}

                                            {/* IMAGE CONTAINER */}
                                            <div
                                                className="relative w-full bg-black cursor-pointer group"
                                                onClick={() => setZoomImage(url)}
                                            >
                                                <img
                                                    src={url}
                                                    alt="Selected"
                                                    className="w-full h-auto object-contain max-h-[500px]"
                                                />
                                            </div>

                                            {/* TECH DOC DISCREPANCY SECTION */}
                                            {showDiscrepancyUI && !previewMode && (
                                                <div className="bg-slate-800 border-t border-slate-700 p-3">
                                                    <label className="flex items-center gap-2 cursor-pointer group">
                                                        <input
                                                            type="checkbox"
                                                            checked={photoMetadata[url]?.hasDiscrepancy || false}
                                                            onChange={(e) => {
                                                                if (onMetadataChange) {
                                                                    onMetadataChange(url, {
                                                                        ...photoMetadata[url],
                                                                        hasDiscrepancy: e.target.checked
                                                                    });
                                                                }
                                                            }}
                                                            className="checkbox checkbox-warning checkbox-sm"
                                                        />
                                                        <span className="text-xs text-yellow-400 font-bold group-hover:text-yellow-300">
                                                            ⚠️ Afwijking (tag/label/tekening)
                                                        </span>
                                                    </label>

                                                    {photoMetadata[url]?.hasDiscrepancy && (
                                                        <DiscrepancyNoteTextarea
                                                            key={`note-${url}`}
                                                            initialValue={photoMetadata[url]?.discrepancyNote || ''}
                                                            onSave={(value) => {
                                                                if (onMetadataChange) {
                                                                    onMetadataChange(url, {
                                                                        ...photoMetadata[url],
                                                                        discrepancyNote: value
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {/* ACTION BUTTONS (BLOCK LAYOUT - HIGH CONTRAST) */}
                                            <div className="flex flex-col gap-3 p-4 bg-slate-900 border-t border-slate-700" style={{ backgroundColor: '#0f172a' }}>

                                                {/* Zoom Button */}
                                                <button
                                                    onClick={() => setZoomImage(url)}
                                                    className="w-full bg-blue-700 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md border border-blue-500/50 transition-colors"
                                                    style={{ backgroundColor: '#1d4ed8', color: 'white' }}
                                                    type="button"
                                                >
                                                    <span className="text-lg">🔍</span> Vergroten
                                                </button>

                                                {/* Delete Button - Two-click inline confirmation */}
                                                {canDelete && (
                                                    pendingDelete === url ? (
                                                        // CONFIRMATION STATE - show confirm/cancel
                                                        <div className="flex gap-2 w-full">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    performSessionDelete(url);
                                                                    setPendingDelete(null);
                                                                }}
                                                                className="flex-1 bg-green-500 hover:bg-green-400 text-black font-black py-3 px-3 rounded-lg flex items-center justify-center gap-1 shadow-lg border-2 border-green-300"
                                                                style={{ backgroundColor: '#22c55e', color: '#000000' }}
                                                                type="button"
                                                            >
                                                                ✓ BEVESTIG
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    setPendingDelete(null);
                                                                }}
                                                                className="flex-1 bg-white hover:bg-slate-100 text-black font-black py-3 px-3 rounded-lg flex items-center justify-center gap-1 shadow-lg border-2 border-slate-300"
                                                                style={{ backgroundColor: '#ffffff', color: '#000000' }}
                                                                type="button"
                                                            >
                                                                ✕ ANNULEER
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        // INITIAL STATE - show delete button
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setPendingDelete(url);
                                                            }}
                                                            className={`w-full text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md transition-colors ${isSession ? 'bg-red-700 hover:bg-red-600 border border-red-500' : 'bg-orange-800 hover:bg-orange-700 border border-orange-600'}`}
                                                            style={{ backgroundColor: isSession ? '#b91c1c' : '#9a3412', color: 'white' }}
                                                            title={isSession ? "Deep Delete" : "Verwijderen uit Bibliotheek"}
                                                            type="button"
                                                        >
                                                            <span className="text-lg">🗑️</span> {isSession ? "VERWIJDEREN (DEEP)" : "VERWIJDEREN"}
                                                        </button>
                                                    )
                                                )}

                                                {/* Deselect Button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onToggleSelect && onToggleSelect(url);
                                                    }}
                                                    className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 border border-slate-600 transition-colors"
                                                    style={{ backgroundColor: '#1e293b', color: '#cbd5e1' }}
                                                    title="Deselecteren"
                                                    type="button"
                                                >
                                                    <span className="text-lg">❌</span> Deselecteren
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Section 2: Upload Area */}
                    {previewMode ? (
                        <div className="border-3 border-dashed border-orange-500/50 rounded-xl bg-orange-900/10 p-8 text-center space-y-4">
                            <div className="w-16 h-16 mx-auto rounded-full bg-orange-950/50 flex items-center justify-center border-2 border-orange-500">
                                <span className="text-3xl">🛡️</span>
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-orange-400 mb-1 tracking-tight">PREVIEW MODUS</h4>
                                <p className="text-slate-400 text-sm max-w-sm mx-auto leading-relaxed">
                                    Echte uploads zijn uitgeschakeld om datavervuiling te voorkomen. Gebruik de knop hieronder om door te gaan met een placeholder.
                                </p>
                            </div>
                            <button
                                onClick={() => onUploadComplete(["https://placehold.co/600x400/1e293b/white?text=SIMULATIE+FOTO"])}
                                className="btn btn-lg bg-orange-600 hover:bg-orange-500 text-black font-black py-4 px-8 shadow-[0_0_20px_rgba(234,88,12,0.3)] animate-pulse active:scale-95 transition-all uppercase tracking-widest border-none"
                            >
                                Overbrug Upload ⚡
                            </button>
                        </div>
                    ) : (
                        <div className={`
                            border-3 border-dashed rounded-xl transition-all duration-300 relative overflow-hidden
                            ${uploading
                                ? 'border-blue-500/50 bg-blue-900/10'
                                : 'border-slate-600 bg-slate-800/30'
                            }
                        `}>
                            <div className="grid grid-cols-1 md:grid-cols-2">
                                {/* Action 1: Gallery */}
                                <div className="relative border-r border-slate-700/50">
                                    <input
                                        type="file"
                                        id={`upload-gallery-${instanceId}`}
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        disabled={uploading}
                                    />
                                    <label
                                        htmlFor={`upload-gallery-${instanceId}`}
                                        className="flex flex-col items-center justify-center py-8 md:py-12 px-4 text-center cursor-pointer hover:bg-slate-800/50 transition-colors group"
                                    >
                                        <div className="w-16 h-16 mb-4 rounded-full bg-slate-700/50 flex items-center justify-center group-hover:bg-slate-600 transition-colors">
                                            <span className="text-3xl">📂</span>
                                        </div>
                                        <h4 className="text-lg font-bold text-slate-300 mb-1 uppercase tracking-wider">Bibliotheek</h4>
                                        <p className="text-[10px] text-slate-500 uppercase font-black">Bestand Kiezen</p>
                                    </label>
                                </div>

                                {/* Action 2: Camera */}
                                <div className="relative bg-blue-600/5">
                                    <input
                                        type="file"
                                        id={`upload-camera-${instanceId}`}
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        disabled={uploading}
                                    />
                                    <label
                                        htmlFor={`upload-camera-${instanceId}`}
                                        className="flex flex-col items-center justify-center py-8 md:py-12 px-4 text-center cursor-pointer hover:bg-blue-600/10 transition-colors group"
                                    >
                                        {uploading ? (
                                            <>
                                                <div className="w-12 h-12 mb-4 rounded-full border-4 border-t-blue-500 border-blue-500/30 animate-spin"></div>
                                                <div className="text-sm font-bold text-blue-400">Verwerken... {Math.round(progress)}%</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-16 h-16 mb-4 rounded-full bg-blue-600/20 flex items-center justify-center group-hover:bg-blue-600/40 transition-colors">
                                                    <span className="text-3xl">📸</span>
                                                </div>
                                                <h4 className="text-lg font-bold text-blue-400 mb-1 uppercase tracking-wider">Camera</h4>
                                                <p className="text-[10px] text-blue-500 uppercase font-black">Directe Foto</p>
                                            </>
                                        )}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Section 3: Available Library */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <div className="flex flex-col gap-4 mb-6">
                            <h3 className="text-lg font-bold text-slate-300 flex items-center gap-2">
                                📚 Beschikbaar in Bibliotheek ({finalAvailableImages.length})
                            </h3>

                            {/* Legacy Upload Button (Hidden/Redundant but kept for structure) */}
                            <div className="relative hidden">
                                <input type="file" id="file-upload" accept="image/*" multiple onChange={handleFileChange} className="hidden" disabled={uploading} />
                            </div>
                        </div>

                        {finalAvailableImages.length === 0 ? (
                            <div className="text-center py-8 text-slate-500 italic">
                                Geen ongebruikte foto's in de bibliotheek.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {finalAvailableImages.map((item) => {
                                    const url = typeof item === 'string' ? item : item.url;
                                    const label = typeof item === 'object' ? item.label : null;

                                    const isSession = isSessionUpload(url);
                                    const canDelete = (allowDelete || isSession) && !previewMode; // Disable delete in preview

                                    return (
                                        <div
                                            key={url}
                                            data-image-card="true"
                                            className="relative rounded-xl overflow-hidden bg-slate-900 border border-slate-700 shadow-md touch-manipulation flex flex-col"
                                        >
                                            {/* LABEL HEADER (NEW - Above Image) */}
                                            {label && (
                                                <div className="bg-blue-900/90 text-white text-xs font-bold px-3 py-2 text-center border-b border-blue-500/50 truncate z-10 relative">
                                                    📌 {label}
                                                </div>
                                            )}

                                            {/* Image Container with Actions */}
                                            <div className="relative w-full bg-black group">
                                                {/* Main Image */}
                                                <img
                                                    src={url}
                                                    alt="Library"
                                                    className="w-full h-auto object-contain transition-opacity cursor-pointer active:opacity-70 max-h-[500px]"
                                                    onClick={() => !previewMode && onToggleSelect && onToggleSelect(url)}
                                                    onError={(e) => {
                                                        console.warn("Image unavailable (likely deleted), removing from view:", url);
                                                        e.currentTarget.style.display = 'none'; // Hide img immediately
                                                        const card = e.currentTarget.closest('[data-image-card]');
                                                        if (card) card.style.display = 'none'; // Hide entire card immediately
                                                        setDeadImageUrls(prev => [...prev, url]); // Update state for clean re-render
                                                    }}
                                                />

                                                {/* Action Buttons Container - Top Right of Image */}
                                                <div className="absolute top-2 right-2 flex gap-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">

                                                    {/* Zoom Button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            setZoomImage(url);
                                                        }}
                                                        className="bg-slate-900/80 hover:bg-blue-600 text-white p-2 rounded-lg shadow-lg border border-white/20 backdrop-blur-md active:scale-95 transition-transform"
                                                        title="Vergroten"
                                                        type="button"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                        </svg>
                                                    </button>

                                                    {/* Delete Button */}
                                                    {canDelete && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();

                                                                const message = isSession
                                                                    ? '⚠️ Foto direct verwijderen uit deze bibliotheek en database?'
                                                                    : '⚠️ Weet u zeker dat u deze foto wilt verwijderen?';

                                                                if (window.confirm(message)) {
                                                                    performDelete(url);
                                                                }
                                                            }}
                                                            className={`text-white p-2 rounded-lg shadow-lg border border-white/20 backdrop-blur-md active:scale-95 transition-transform ${isSession ? 'bg-red-600/80 hover:bg-red-500' : 'bg-orange-700/80 hover:bg-orange-600'}`}
                                                            title={isSession ? "Deep Delete" : "Verwijderen"}
                                                            type="button"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                                {/* NEW Badge - Top Left */}
                                                {isSession && (
                                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md z-10 pointer-events-none">
                                                        NIEUW
                                                    </div>
                                                )}
                                            </div>

                                            {/* Selection Indicator Overlay - Bottom */}
                                            <div
                                                className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-black/80 to-transparent flex items-end justify-center pb-2 pointer-events-none"
                                            >
                                                <span className="text-[10px] text-white/90 font-medium">
                                                    Selecteer
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {error && <p className="text-sm text-red-400 text-center">{error}</p>}
                </>
            )
            } {/* End Standard Mode */}
        </div >
    );
}
